/*
 * @Author: Ryan
 * @Date: 2026-01-08
 * @Description: Landing 页面通用头部组件
 */
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Drawer, Dropdown } from 'antd';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useContext, useEffect, useState } from 'react';

import { RightContext } from '@/components/RightProvider/context';
import {
  aiReelGeneratorURL,
  aiRemovalURL,
  aiTiktokVideoURL,
  aiTwinGeneratorURL,
  aiUgcVideoURL,
  aiVideoEditURL,
  avatarVideoProURL,
  dropshippingVideoAdsURL,
  facebookVideoAdURL,
  faceSwapperURL,
  imageEnhanceURL,
  imageGenerateURL,
  imageTranslationURL,
  pricingURL,
  productAvatarURL,
  seedance20URL,
  sora2ProURL,
  videoGenerateURL,
  videoTranslateURL,
  voiceCloneURL,
} from '@/utils/routeConstants';

import NativeButton from '../NativeButton';
import styles from './index.module.scss';

// 动态导入 UserAccount，避免 antd 组件阻塞首屏渲染
const UserAccount = dynamic(() => import('@/components/UIComponents/UserAccount'), {
  ssr: false,
  loading: () => <div className={styles.userPlaceholder} />,
});

// 导航菜单数据
const NAV_MENUS = [
  {
    key: 'ai-video',
    label: 'AI Video',
    children: [
      {
        key: 'ai-video-generator',
        label: 'AI Video Generator',
        href: videoGenerateURL,
      },
      {
        key: 'ai-video-editor',
        label: 'AI Video Editor',
        href: aiVideoEditURL,
      },
      {
        key: 'video-translator',
        label: 'Video Translation',
        href: videoTranslateURL,
      },
      {
        key: 'face-swap',
        label: 'Face Swap',
        href: faceSwapperURL,
      },
    ],
  },
  {
    key: 'ai-image',
    label: 'AI Image',
    children: [
      {
        key: 'ai-image-generator',
        label: 'AI Image Generator',
        href: imageGenerateURL,
      },
      {
        key: 'ai-image-translator',
        label: 'Image Translation',
        href: imageTranslationURL,
      },
      {
        key: 'ai-image-enhancer',
        label: 'Image Enhance',
        href: imageEnhanceURL,
      },
      {
        key: 'video-subtitle-remover',
        label: 'AI Removal',
        href: aiRemovalURL,
      },
    ],
  },
  {
    key: 'ai-avatar',
    label: 'AI Avatar',
    children: [
      {
        key: 'ai-avatar-generator',
        label: 'AI Avatar Generator',
        href: avatarVideoProURL,
      },
      {
        key: 'ai-product-avatar',
        label: 'Product Avatar',
        href: productAvatarURL,
      },
      {
        key: 'ai-voice-cloning',
        label: 'Voice Clone',
        href: voiceCloneURL,
      },
    ],
  },
  {
    key: 'use-cases',
    label: 'Use Cases',
    children: [
      {
        key: 'ai-tiktok-video-generator',
        label: 'AI TikTok Video Generator',
        href: aiTiktokVideoURL,
      },
      {
        key: 'ai-ugc-video-generator',
        label: 'AI UGC Video Generator',
        href: aiUgcVideoURL,
      },
      {
        key: 'ai-twin-generator',
        label: 'AI Twin Generator',
        href: aiTwinGeneratorURL,
      },
      {
        key: 'sora-2-pro',
        label: 'Sora 2 Pro',
        href: sora2ProURL,
      },
      {
        key: 'seedance-2-0',
        label: 'Seedance 2.0',
        href: seedance20URL,
      },
      {
        key: 'facebook-video-ad-creator',
        label: 'Facebook Video Ad Creator',
        href: facebookVideoAdURL,
      },
      {
        key: 'dropshipping-video-ads-maker',
        label: 'Dropshipping Video Ads Maker',
        href: dropshippingVideoAdsURL,
      },
      {
        key: 'ai-reel-generator',
        label: 'AI Reel Generator',
        href: aiReelGeneratorURL,
      },
    ],
  },
  {
    key: 'pricing',
    label: 'Pricing',
    href: pricingURL,
  },
];

interface LandingHeaderProps {
  transparent?: boolean; // 首屏透明，滚动后变白
}

const LandingHeader: React.FC<LandingHeaderProps> = ({ transparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(null);
  const rightContext = useContext(RightContext);

  // 确保只在客户端检查登录状态
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!transparent) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [transparent]);

  const bgClass = transparent && !isScrolled ? styles.transparent : styles.solid;
  const isLoggedIn = isClient && rightContext.getIsLoggedIn();

  const handleLogin = async () => {
    await rightContext.getUserLoginState({
      onReady: () => {
        // 登录成功后刷新页面或其他操作
      },
    });
  };

  // 切换移动端子菜单展开状态
  const toggleMobileSubMenu = (key: string) => {
    setExpandedMobileMenu(expandedMobileMenu === key ? null : key);
  };

  // PC 端下拉菜单渲染
  const renderDropdownMenu = (
    children: { key: string; label: string; href: string }[],
  ): MenuProps => {
    const items: MenuProps['items'] = children.map((item) => ({
      key: item.key,
      label: (
        <Link href={item.href} className={styles.dropdownLink}>
          {item.label}
        </Link>
      ),
    }));
    return { items };
  };

  // 移动端抽屉菜单
  const renderMobileMenu = () => (
    <Drawer
      placement="top"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      height="auto"
      className={styles.mobileDrawer}
      closable={false}
      styles={{ body: { padding: 0 } }}
      rootClassName={styles.noTransition}
    >
      {/* 抽屉头部 */}
      <div className={styles.drawerHeader}>
        <Link href="/" className={styles.logo} onClick={() => setMobileMenuOpen(false)}>
          <Image
            src="/images/logo-with-name.png"
            alt="FalcoCut"
            width={120}
            height={32}
          />
        </Link>
        <div className={styles.drawerHeaderRight}>
          <button className={styles.closeBtn} onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* 抽屉菜单内容 */}
      <div className={styles.drawerContent}>
        {NAV_MENUS.map((menu) => (
          <div key={menu.key} className={styles.mobileMenuItem}>
            {menu.children ? (
              <>
                <button
                  className={`${styles.mobileMenuTitle} ${expandedMobileMenu === menu.key ? styles.expanded : ''}`}
                  onClick={() => toggleMobileSubMenu(menu.key)}
                >
                  <span>{menu.label}</span>
                  <RightOutlined
                    className={`${styles.menuArrow} ${expandedMobileMenu === menu.key ? styles.rotated : ''}`}
                  />
                </button>
                {expandedMobileMenu === menu.key && (
                  <div className={styles.mobileSubMenu}>
                    {menu.children.map((child) => (
                      <Link
                        key={child.key}
                        href={child.href}
                        className={styles.mobileSubMenuItem}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                href={menu.href || '/'}
                className={styles.mobileMenuTitle}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>{menu.label}</span>
              </Link>
            )}
          </div>
        ))}
      </div>
    </Drawer>
  );

  return (
    <header className={`${styles.header} ${bgClass}`}>
      <div className={styles.container}>
        {/* Logo */}
        <Link href="/" className={styles.logo}>
          <Image
            src="/images/logo-with-name.png"
            alt="FalcoCut"
            width={120}
            height={32}
            priority
          />
        </Link>

        {/* PC 端导航菜单 */}
        <nav className={styles.nav}>
          {NAV_MENUS.map((menu) =>
            menu.children ? (
              <Dropdown
                key={menu.key}
                menu={renderDropdownMenu(menu.children)}
                trigger={['hover']}
                overlayClassName={styles.dropdownOverlay}
              >
                <button className={styles.navItem}>
                  <span>{menu.label}</span>
                  <DownOutlined className={styles.navArrow} />
                </button>
              </Dropdown>
            ) : (
              <Link key={menu.key} href={menu.href || '/'} className={styles.navItem}>
                {menu.label}
              </Link>
            ),
          )}
        </nav>

        {/* 右侧操作区 */}
        <div className={styles.actions}>
          {isLoggedIn ? (
            <UserAccount />
          ) : (
            <NativeButton
              type="primary"
              className={styles.signUpBtn}
              onClick={handleLogin}
            >
              Sign up
            </NativeButton>
          )}

          {/* 移动端汉堡菜单 */}
          <div className={styles.mobileActions}>
            <button
              className={styles.hamburgerBtn}
              onClick={() => setMobileMenuOpen(true)}
            >
              <HamburgerIcon />
            </button>
          </div>
        </div>
      </div>

      {/* 移动端抽屉菜单 */}
      {renderMobileMenu()}
    </header>
  );
};

// 汉堡菜单图标
const HamburgerIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 6h18M3 12h18M3 18h18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// 关闭图标
const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default LandingHeader;
